// INTEL CONFIDENTIAL
// Copyright (C) 2023 Intel Corporation

import React from 'react'
import './Pricing.scss'
import { initialTextBlocks, type TextBlockTemplate, type TextContent } from './Block.types'
import { type ResourceAction } from '../resourceAIandRD/Resource.types'

interface TextBlockProps {
  data: TextBlockTemplate
}

const TextBlock: React.FC<TextBlockProps> = (props): JSX.Element => {
  const renderAction = (title: string, action: ResourceAction | undefined): JSX.Element => {
    if (!action) return <></>
    return (
      <a
        className={action.type === 'link' ? 'link' : `btn btn-${action?.variant ?? 'outline-primary'}`}
        href={action.redirectTo}
        target={action.openInNewTab ? '_blank' : '_self'}
        rel={action.openInNewTab ? 'noreferrer' : undefined}
        style={action.style ?? undefined}
        intc-id={`${action.type}-${action.label}-${title}`.replaceAll(' ', '-')}
        data-wap_ref={`${action.type}-${action.label}-${title}`.replaceAll(' ', '-')}
        role={action.type}
        aria-label={`Go to ${action.redirectTo}`}
      >
        {action.label}
      </a>
    )
  }

  const renderContent = (content: TextContent): JSX.Element => {
    if (content.type === 'text') return <span>{content.content}</span>
    else {
      return (
        <ul className="pricing-item-list gap-s3 ps-0">
          {content.content.map((e: string, idx: number) => (
            <li key={idx}>{e}</li>
          ))}
        </ul>
      )
    }
  }

  const data: TextBlockTemplate = props.data
  return (
    <div className="pricing-item-container p-s8 gap-s6">
      <div className="pricing-item-title gap-s3">
        <span className="h6">{data?.subTitleTop}</span>
        <h3 className="h4">{data?.title}</h3>
        <span className="h6">{data?.subTitle}</span>
      </div>
      {renderContent(data.text)}
      <div className="pricing-item-cta py-s4 mt-auto">{renderAction(data?.title, data.action)}</div>
    </div>
  )
}

const Pricing: React.FC = (): JSX.Element => {
  return (
    <div className="pricing-container px-s8 py-s9 px-sm-s10 py-sm-s11">
      <div className="internal-container gap-s8">
        <div className="pricing-title-container gap-s4">
          <h2>
            <span className="display-4">Pricing</span>
          </h2>
          <span className="lead">
            Intel Tiber AI Cloud offers a wide variety of AI-optimized computing instances. From GPU-enabled VMs to
            large-scale clusters with AI processors.
          </span>
        </div>
        <div className="pricing-items-container column-gap-s8">
          {initialTextBlocks.map((data: TextBlockTemplate, idx: number) => (
            <TextBlock data={data} key={idx} />
          ))}
        </div>
      </div>
    </div>
  )
}

export default Pricing
